import { PureComponent } from "react";

import { QESButton, QESTextInput, QESTextArea } from "@app/shared/qes-standards";
import { QESCheckbox } from "@app/shared/qes-standards/forms/Checkbox";
import { FilterCriteria, FilterCriteriaModel, LoadingSpinner } from "@app/shared";

import { ResourceType, ResourceService } from "@app/resources";

import { ProviderSet } from "@app/provider-sets/providerSet";
import { FlyoutContextOutgoingProps, FlyoutFooter, withFlyoutContext } from "@app/shared/Flyouts";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";

interface ClonePanelProps {
  providerSet: ProviderSet;
  sourceProviderSet: ProviderSet;
  filterCriteria: FilterCriteriaModel[];
  filterCriteriaColumnNames?: string[];
  loading: boolean;
  handleSave: (name: string, description: string, isPrivate: boolean, filterCriteria: FilterCriteriaModel[], filterInvalidRecords: boolean) => void;
  hasProviderValidations?: boolean;
}

interface ClonePanelState {
  description: string;
  filterCriteria: FilterCriteriaModel[];
  filterCriteriaHasErrors: boolean;
  filterInvalidRecords: boolean;
  isPrivate: boolean;
  loading: boolean;
  name: string;
  nameIsValid: boolean;
  nameIsValidText: string;
}

class WrappedClonePanelContent extends PureComponent<ClonePanelProps & FlyoutContextOutgoingProps, ClonePanelState> {

  private readonly resourceService = new ResourceService();

  private nameValidationInterval: number = null;

  constructor(props: ClonePanelProps & FlyoutContextOutgoingProps) {
    super(props);
    this.state = {
      description: "",
      filterCriteria: this.props.filterCriteria,
      filterCriteriaHasErrors: false,
      filterInvalidRecords: false,
      isPrivate: false,
      loading: false,
      name: "",
      nameIsValid: true,
      nameIsValidText: "",
    };
  }

  public componentWillUnmount() {
    window.clearInterval(this.nameValidationInterval);
  }

  private validateName = async (name: string) => {
    window.clearInterval(this.nameValidationInterval);
    this.nameValidationInterval = window.setTimeout(async () => {
      if (!name) {
        this.setState({
          nameIsValid: false,
          nameIsValidText: ""
        });
      } else if (await this.resourceService.resourceNameExists([ResourceType.ProviderSet, ResourceType.FilteredProviderSet], name)) {
        this.setState({
          nameIsValid: false,
          nameIsValidText: "This name has already been used."
        });
      } else {
        this.setState({
          nameIsValid: true,
          nameIsValidText: ""
        });
      }
    }, 500);
  }

  public render() {
    return (
      <>
        {this.props.loading || this.state.loading ? <LoadingSpinner /> :
          <>
            <div className="col-7">
              <QESTextInput
                testId={"clone-provider-set-panel-content__clone-from"}
                containerClassName="mb-3"
                label="Clone From"
                value={this.props.providerSet.resource.name}
                disabled={true}
              />
              <QESTextInput
                testId={"clone-provider-set-panel-content__source-network"}
                containerClassName="mb-3"
                label="Source Network"
                value={this.props.sourceProviderSet.resource.name}
                disabled={true}
              />
              <QESTextInput
                testId={"clone-provider-set-panel-content__name"}
                containerClassName="mb-3"
                label="Name"
                value={this.state.name}
                onChange={(e) => {
                  this.setState({ name: e.target.value });
                  this.validateName(e.target.value);
                }}
                isValid={this.state.nameIsValid}
                errorText={this.state.nameIsValidText}
                required={true}
              />
              <QESTextArea
                testId={"clone-provider-set-panel-content__description"}
                containerClassName="mb-3"
                label="Description"
                value={this.state.description}
                onChange={(e) => this.setState({ description: e.target.value })}
                isValid={!!this.state.description && !!this.state.description.trim()}
                required={true}
              />
              <QESCheckbox
                testId={"clone-provider-set-panel-content__private"}
                containerClassName="mb-3"
                label="This is a Private Resource (can be administered only by you)"
                checked={this.state.isPrivate}
                onChange={(e) => this.setState({ isPrivate: e.target.checked })} />
              <FilterCriteria
                fieldNames={this.props.filterCriteriaColumnNames}
                defaultCriteria={this.props.filterCriteria}
                onChange={(response) => {
                  this.setState({
                    filterCriteria: response.criteria,
                    filterCriteriaHasErrors: response.hasErrors
                  })
                }} />
              {this.props.hasProviderValidations ?
                <RoleRestricted allowedRoles={[Roles.PROVIDER_VALIDATION_USER]}>
                  <QESCheckbox
                    testId={"create-provider-set-panel-content__invalidation-filter"}
                    label="Filter out invalid records"
                    checked={this.state.filterInvalidRecords}
                    onChange={(e) => this.setState({ filterInvalidRecords: e.target.checked })}
                    containerClassName="mb-3"
                  />
                </RoleRestricted>
                : null
              }
            </div>
            <FlyoutFooter
              submitButton={
                <QESButton qesTestId="clone-provider-set-panel-content__confirm"
                  disabled={!this.state.name || !this.state.nameIsValid || !this.state.description}
                  onClick={() => {
                    this.props.handleSave(this.state.name, this.state.description, this.state.isPrivate, this.state.filterCriteria, this.state.filterInvalidRecords);
                    this.props.handleCloseFlyout();
                  }}
                >Save</QESButton>
              }
            />
          </>
        }
      </>
    );
  }
}

const ClonePanelContent = withFlyoutContext(WrappedClonePanelContent);
export default ClonePanelContent;
